import { connect } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import { EVENTS_BLOCK_ID, getEventBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErpFormEvent } from '../interfaces/IKnmErpFormEvent';

interface IKnmErpEventsProps {
  watchOnly?: boolean;
}

interface IKnmErpEventsStoreProps {
  formRef: IKnmFormRef | undefined;
}

const KnmErpEventsWithoutStore = ({ watchOnly, formRef }: IKnmErpEventsProps & IKnmErpEventsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addKnmEvent = useCallback(() => !watchOnly && push && push('events', { listId: uuidv4() }), [push, watchOnly]);
  const makeRemoveKnmEventHandler = (index: number) => () => !watchOnly && remove && remove('events', index);

  return (
    <FieldBlock
      label="Перечень мероприятий по контролю, необходимых для достижения целей и задач проведения КНМ"
      id={EVENTS_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          type="button"
          id="knmErpEventsAddButton"
          variant="link"
          color="primary"
          text="Добавить"
          disabled={watchOnly}
          onClick={addKnmEvent}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const eventsFieldError = props.errors?.events?.[ARRAY_ERROR];
          const isEventsFieldTouched = props.touched?.events;

          if (eventsFieldError && isEventsFieldTouched) {
            return <p className="shared-input-error">{eventsFieldError}</p>;
          }

          return null;
        }}
      </FormSpy>

      <FieldArray name="events">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErpFormEvent = fields.value?.[index];
            const id = getEventBlockId(fieldValue.listId);

            return (
              <Row key={id} notWrap>
                <Row>
                  <Row>
                    <FieldBlock>
                      <TextareaField name={`${name}.name`} id={`${name}.name`} disabled={watchOnly} />
                    </FieldBlock>
                  </Row>
                  <Row>
                    <FieldBlock label="Дата начала мероприятия" placementLabel="left" size="half" color="black">
                      <DatePickerField
                        className="shared-row-field"
                        name={`${name}.startDate`}
                        disabled={watchOnly}
                        minDate={new Date('01-01-2014')}
                      />
                    </FieldBlock>
                    <FieldBlock label="Дата окончания мероприятия" placementLabel="left" size="half" color="black">
                      <DatePickerField
                        className="shared-row-field"
                        name={`${name}.stopDate`}
                        disabled={watchOnly}
                        minDate={new Date('01-01-2014')}
                      />
                    </FieldBlock>
                  </Row>
                </Row>
                <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveKnmEventHandler(index)} />
              </Row>
            );
          });
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpEventsStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const KnmErpEvents = connect(mapStateToProps)(KnmErpEventsWithoutStore);
